import { z } from 'zod';

const optionalNumber = (schema: z.ZodNumber) =>
  z.preprocess((value) => {
    if (value === '' || value === null || value === undefined) return undefined;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, schema.optional());

const optionalString = (schema: z.ZodString) =>
  z.preprocess((value) => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? undefined : trimmed;
    }
    if (value === null || value === undefined) return undefined;
    return value;
  }, schema.optional());

// Quote builder validation schema
export const quoteBuilderSchema = z.object({
  // Lead & Customer
  lead_id: z.string().optional(),
  customer_id: z.string().min(1, 'Customer is required'),
  has_co_buyer: z.boolean().optional(),
  co_buyer_customer_id: z.string().optional(),
  location_id: z.string().min(1, 'Location is required'),

  // Quoted item (SP4 slice 3: boat | engine | trailer; manual entry is boat-only)
  item_type: z.enum(['boat', 'engine', 'trailer']).default('boat'),
  boat_source: z.enum(['inventory', 'manual']).default('inventory'),
  /** Holds the selected inventory item id for ALL item types (legacy field name). */
  boat_id: z.string().optional(),
  manual_make: optionalString(z.string()),
  manual_model: optionalString(z.string()),
  manual_year: optionalNumber(z.number().int().min(1900, 'Year is required')),
  manual_vin_hin: optionalString(z.string()),
  manual_stock_number: optionalString(z.string()),
  manual_length_feet: optionalNumber(z.number().min(0)),
  manual_length_inches: optionalNumber(z.number().min(0)),

  title: z.string().min(1, 'Title is required').max(255),
  description: optionalString(z.string()),

  // Pricing
  boat_price: optionalNumber(z.number().min(0, 'Boat price must be positive')),
  trailer_price: optionalNumber(z.number().min(0)),
  accessories_total: optionalNumber(z.number().min(0)),
  trade_in_value: optionalNumber(z.number().min(0)),
  tax_amount: optionalNumber(z.number().min(0)),
  trailer_tax: optionalNumber(z.number().min(0)),
  doc_fee: optionalNumber(z.number().min(0)),
  freight_fee: optionalNumber(z.number().min(0)),
  prep_fee: optionalNumber(z.number().min(0)),
  boat_registration_fee: optionalNumber(z.number().min(0)),
  trailer_registration_fee: optionalNumber(z.number().min(0)),
  boat_cost: optionalNumber(z.number().min(0)),

  // Trade-in details (optional)
  trade_in_year: optionalNumber(z.number().int().min(1900)),
  trade_in_make: optionalString(z.string()),
  trade_in_model: optionalString(z.string()),
  trade_in_vin_hin: optionalString(z.string()),
  trade_in_acv: optionalNumber(z.number().min(0)),
  trade_in_allowance: optionalNumber(z.number().min(0)),
  trade_in_payoff: optionalNumber(z.number().min(0)),

  // Financing
  down_payment: optionalNumber(z.number().min(0)),
  loan_term_months: optionalNumber(z.number().int().min(1).max(360)),
  interest_rate: optionalNumber(z.number().min(0).max(100)),

  // Validity
  valid_from: optionalString(z.string()),
  valid_until: optionalString(z.string()),
}).superRefine((data, ctx) => {
    if (data.has_co_buyer) {
      if (!data.co_buyer_customer_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Co-buyer is required when enabled',
          path: ['co_buyer_customer_id'],
        });
      } else if (data.co_buyer_customer_id === data.customer_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Co-buyer must be different from the customer',
          path: ['co_buyer_customer_id'],
        });
      }
    }

    if (data.boat_source === 'inventory') {
      if (!data.boat_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Inventory ${data.item_type ?? 'boat'} is required`,
          path: ['boat_id'],
        });
      }
    } else {
      if (!data.manual_make) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Make is required for manual boats',
          path: ['manual_make'],
        });
      }
      if (!data.manual_model) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Model is required for manual boats',
          path: ['manual_model'],
        });
      }
      if (!data.manual_year) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Year is required for manual boats',
          path: ['manual_year'],
        });
      }
    }
    const pricingInputs = [
      data.boat_price,
      data.trailer_price,
      data.accessories_total,
      data.tax_amount,
      data.trailer_tax,
      data.doc_fee,
      data.freight_fee,
      data.prep_fee,
      data.boat_registration_fee,
      data.trailer_registration_fee,
      data.trade_in_allowance,
      data.trade_in_value,
      data.trade_in_payoff,
    ];
    const hasPricingInput = pricingInputs.some((value) => value !== undefined && value !== null && value !== 0);
    if (hasPricingInput && data.down_payment) {
      const boatPrice = data.boat_price ?? 0;
      const trailerPrice = data.trailer_price ?? 0;
      const accessoriesTotal = data.accessories_total ?? 0;
      const tradeInValue = data.trade_in_allowance ?? data.trade_in_value ?? 0;
      const tradePayoff = data.trade_in_payoff ?? 0;
      const docFee = data.doc_fee ?? 0;
      const freightFee = data.freight_fee ?? 0;
      const prepFee = data.prep_fee ?? 0;
      const boatRegistrationFee = data.boat_registration_fee ?? 0;
      const trailerRegistrationFee = data.trailer_registration_fee ?? 0;
      const taxAmount = data.tax_amount ?? 0;
      const trailerTax = data.trailer_tax ?? 0;

      const price = boatPrice + trailerPrice + accessoriesTotal;
      const tradeEquity = tradeInValue - tradePayoff;
      const netPrice = price - tradeEquity;
      const feesAndTaxes =
        docFee + freightFee + prepFee + boatRegistrationFee + trailerRegistrationFee + taxAmount + trailerTax;
      const total = netPrice + feesAndTaxes;

      if (data.down_payment > total) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Down payment cannot exceed total amount',
          path: ['down_payment'],
        });
      }
    }

    const validFrom = data.valid_from ? Date.parse(data.valid_from) : null;
    const validUntil = data.valid_until ? Date.parse(data.valid_until) : null;
    if (data.valid_from && Number.isNaN(validFrom)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Valid from date is invalid',
        path: ['valid_from'],
      });
    }
    if (data.valid_until && Number.isNaN(validUntil)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Valid until date is invalid',
        path: ['valid_until'],
      });
    }
    if (validFrom !== null && validUntil !== null && !Number.isNaN(validFrom) && !Number.isNaN(validUntil)) {
      if (validUntil <= validFrom) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Valid until date must be after valid from date',
          path: ['valid_until'],
        });
      }
    }
  });

export type QuoteBuilderSchemaType = z.infer<typeof quoteBuilderSchema>;
